import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import {
  Badge,
  Button,
  Card,
  CustomDialog,
  DialogContainer,
  TextField as Input,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  ExternalLink,
  Loader2,
  MessageSquare,
  Search,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from 'lucide-react'
import { z } from 'zod'
import {
  adminDeleteConversation,
  getAdminConversationTranscript,
  getAdminUserPrompts,
} from '@rpc/admin.rpc'
import { MESSAGE_FEEDBACK_REASON_LABELS } from '@shared/chat-types'
import type { FormEvent } from 'react'
import { fullWidthStyles, statusBadgeStyles } from '@/styles/s2-macros'

const adminConversationsSearchSchema = z.object({
  query: z.string().optional(),
  conversationId: z.string().optional(),
})

type DeleteTarget = {
  conversationId: string
  title: string
  prompt: string
}

export const Route = createFileRoute('/admin/conversations')({
  validateSearch: (search) => adminConversationsSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    query: search.query,
    conversationId: search.conversationId,
  }),
  loader: async ({ deps }) => {
    const [promptsData, selectedConversation] = await Promise.all([
      getAdminUserPrompts({ data: { query: deps.query } }),
      deps.conversationId
        ? getAdminConversationTranscript({
            data: { conversationId: deps.conversationId },
          })
        : Promise.resolve(null),
    ])

    return { promptsData, selectedConversation }
  },
  component: AdminConversationsPage,
})

function AdminConversationsPage() {
  const { promptsData, selectedConversation } = Route.useLoaderData()
  const routeSearch = Route.useSearch()
  const router = useRouter()
  const [search, setSearch] = useState(routeSearch.query ?? '')
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [deleteMode, setDeleteMode] = useState<'soft' | 'permanent' | null>(
    null,
  )

  const handleSearch = (event: FormEvent) => {
    event.preventDefault()
    router.navigate({
      to: '/admin/conversations',
      search: {
        query: search.trim() || undefined,
        conversationId: undefined,
      },
      replace: true,
    })
  }

  const handleDelete = async (
    target: DeleteTarget,
    mode: 'soft' | 'permanent',
  ) => {
    setDeleteMode(mode)
    try {
      await adminDeleteConversation({
        data: {
          conversationId: target.conversationId,
          mode,
        },
      })

      setDeleteTarget(null)
      if (
        mode === 'permanent' &&
        routeSearch.conversationId === target.conversationId
      ) {
        await router.navigate({
          to: '/admin/conversations',
          search: { query: routeSearch.query, conversationId: undefined },
          replace: true,
        })
      }
      router.invalidate()
    } catch {
      alert('Conversation delete failed')
    } finally {
      setDeleteMode(null)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Conversation prompts
          </h2>
          <p className="text-neutral-500">
            Review user input prompts, open the conversation transcript, or
            remove conversations.
          </p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="w-full sm:w-80">
            <Input
              aria-label="Search conversation prompts"
              placeholder="Search prompt, user, or conversation ID..."
              value={search}
              onChange={setSearch}
            />
          </div>
          <Button
            type="submit"
            size="S"
            variant="secondary"
            fillStyle="outline"
            aria-label="Search prompts"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {selectedConversation ? (
        <ConversationTranscript
          transcript={selectedConversation}
          onDelete={() =>
            setDeleteTarget({
              conversationId: selectedConversation.conversation.id,
              title:
                selectedConversation.conversation.title ||
                'Untitled conversation',
              prompt: selectedConversation.messages[0]?.content || '',
            })
          }
        />
      ) : null}

      <Card styles={fullWidthStyles}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/70">
              <tr>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Prompt
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  User
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Conversation
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Received
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {promptsData.prompts.map((row) => (
                <tr
                  key={row.messageId}
                  className="transition-colors hover:bg-neutral-50/70 dark:hover:bg-neutral-900/40"
                >
                  <td className="max-w-[34rem] px-6 py-4">
                    <div className="line-clamp-3 text-neutral-900 dark:text-neutral-100">
                      {row.prompt}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-neutral-900 dark:text-neutral-100">
                      {formatOwnerName(row)}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {formatOwnerDetail(row)}
                    </div>
                    {row.userIpAddress ? (
                      <div className="font-mono text-[11px] text-neutral-400">
                        {row.userIpAddress}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="max-w-56 truncate font-medium">
                        {row.conversationTitle || 'Untitled conversation'}
                      </span>
                      {row.conversationDeletedAt ? (
                        <Badge
                          variant="negative"
                          fillStyle="subtle"
                          styles={statusBadgeStyles}
                        >
                          Deleted
                        </Badge>
                      ) : (
                        <Badge
                          variant="positive"
                          fillStyle="subtle"
                          styles={statusBadgeStyles}
                        >
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-neutral-400">
                      {row.conversationId}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-neutral-500">
                    {formatDateTime(row.promptCreatedAt)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="S"
                        variant="secondary"
                        fillStyle="outline"
                        aria-label="Delete conversation"
                        onPress={() =>
                          setDeleteTarget({
                            conversationId: row.conversationId,
                            title:
                              row.conversationTitle || 'Untitled conversation',
                            prompt: row.prompt,
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Link
                        to="/admin/conversations"
                        search={{
                          query: routeSearch.query,
                          conversationId: row.conversationId,
                        }}
                        className="inline-flex h-8 items-center gap-2 rounded-md border border-neutral-200 px-3 text-xs font-medium text-neutral-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 dark:border-neutral-800 dark:text-neutral-200 dark:hover:border-blue-900 dark:hover:bg-blue-950/40"
                      >
                        Open
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {promptsData.prompts.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-neutral-500"
                  >
                    No user prompts found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-neutral-200 bg-neutral-50/80 p-4 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/70">
          <span>Total prompts: {promptsData.total}</span>
          <span>Showing latest {promptsData.prompts.length}</span>
        </div>
      </Card>

      {deleteTarget ? (
        <DialogContainer onDismiss={() => setDeleteTarget(null)}>
          <CustomDialog isDismissible aria-label="Delete conversation">
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  Delete conversation?
                </h2>
                <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
                  Choose soft delete to hide it from the user by setting
                  deletedAt. Choose permanent delete to remove the conversation
                  and all messages from the database.
                </p>
              </div>
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
                <div className="font-medium">{deleteTarget.title}</div>
                <div className="mt-1 line-clamp-3 text-neutral-500">
                  {deleteTarget.prompt || deleteTarget.conversationId}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="secondary"
                  fillStyle="outline"
                  onPress={() => setDeleteTarget(null)}
                  isDisabled={deleteMode !== null}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  onPress={() => handleDelete(deleteTarget, 'soft')}
                  isDisabled={deleteMode !== null}
                >
                  {deleteMode === 'soft' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Soft delete'
                  )}
                </Button>
                <Button
                  variant="negative"
                  onPress={() => handleDelete(deleteTarget, 'permanent')}
                  isDisabled={deleteMode !== null}
                >
                  {deleteMode === 'permanent' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Delete permanently'
                  )}
                </Button>
              </div>
            </div>
          </CustomDialog>
        </DialogContainer>
      ) : null}
    </div>
  )
}

function ConversationTranscript({
  transcript,
  onDelete,
}: {
  transcript: NonNullable<
    Awaited<ReturnType<typeof getAdminConversationTranscript>>
  >
  onDelete: () => void
}) {
  const { conversation, messages } = transcript

  return (
    <Card styles={fullWidthStyles}>
      <div className="border-b border-neutral-200 bg-neutral-50/80 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-900/70">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
              <MessageSquare className="h-3.5 w-3.5" />
              Selected conversation
            </div>
            <h3 className="mt-2 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              {conversation.title || 'Untitled conversation'}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {formatOwnerName(conversation)} ·{' '}
              {formatDateTime(conversation.createdAt)}
            </p>
            <p className="mt-1 font-mono text-[11px] text-neutral-400">
              {conversation.id}
            </p>
          </div>
          <Button
            size="S"
            variant="negative"
            fillStyle="outline"
            onPress={onDelete}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
      <div className="max-h-[34rem] space-y-4 overflow-y-auto p-6">
        {messages.map((message) => (
          <div
            key={message.id}
            className={
              message.role === 'user'
                ? 'rounded-2xl border border-blue-100 bg-blue-50/70 p-4 dark:border-blue-950 dark:bg-blue-950/30'
                : 'rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950'
            }
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                {message.role}
              </span>
              <span className="text-xs text-neutral-400">
                {formatDateTime(message.createdAt)}
              </span>
            </div>
            <div className="whitespace-pre-wrap text-sm leading-6 text-neutral-800 dark:text-neutral-100">
              {message.content}
            </div>
            {message.feedback ? (
              <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      message.feedback.rating === 'positive'
                        ? 'positive'
                        : 'negative'
                    }
                    fillStyle="subtle"
                    styles={statusBadgeStyles}
                  >
                    {message.feedback.rating === 'positive' ? (
                      <ThumbsUp className="h-3.5 w-3.5" />
                    ) : (
                      <ThumbsDown className="h-3.5 w-3.5" />
                    )}
                    {message.feedback.rating}
                  </Badge>
                  <Badge
                    variant="neutral"
                    fillStyle="outline"
                    styles={statusBadgeStyles}
                  >
                    {message.feedback.status}
                  </Badge>
                </div>
                {message.feedback.reasonTags.length > 0 ? (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {message.feedback.reasonTags.map((tag: string) => (
                      <span
                        key={tag}
                        className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-neutral-600 dark:bg-neutral-950 dark:text-neutral-300"
                      >
                        {MESSAGE_FEEDBACK_REASON_LABELS[tag] ?? tag}
                      </span>
                    ))}
                  </div>
                ) : null}
                {message.feedback.note ? (
                  <div className="whitespace-pre-wrap text-xs leading-5 text-neutral-700 dark:text-neutral-300">
                    {message.feedback.note}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  )
}

function formatDateTime(value?: string | Date | null) {
  if (!value) return 'Unknown'
  return new Date(value).toLocaleString()
}

function formatOwnerName(row: {
  userName?: string | null
  userEmail?: string | null
  anonymousId?: string | null
}) {
  if (row.userName) return row.userName
  if (row.userEmail) return row.userEmail
  return 'Anonymous user'
}

function formatOwnerDetail(row: {
  userId?: string | null
  userEmail?: string | null
  anonymousId?: string | null
}) {
  if (row.userEmail) return row.userEmail
  if (row.userId) return row.userId
  if (row.anonymousId) return `anon:${row.anonymousId.slice(0, 12)}`
  return 'No owner recorded'
}
